import React, { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  ShieldAlert,
  TrendingDown,
  Package,
  PlayCircle,
  Info,
  Download,
  ChevronRight,
  ChevronDown,
  Link as LinkIcon,
  Unlink,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Oct 2026 — Stock Reconciliation Tool (super-admin only).
 *
 * Detection is now **ASN-based**: for every released / received ASN,
 * every stock_event booked against (asn × product) is grouped. Excess
 * = total booked − scanned qty on the ASN line. No time window.
 * Rows that cannot be linked to an ASN are listed separately and
 * never counted.
 *
 * Flow: dry-run → review ASN rows + product rollups + unlinked → approve
 * → apply → confirm. The apply step is UNCHANGED and still writes one
 * `reconciliation_adjustment` (or `_out`) ledger row per approved product
 * tagged `source="manual_reconciliation_288272"`. No row is ever edited
 * or deleted.
 */

const confidenceStyles = {
  high: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  needs_manual_review: 'bg-amber-100 text-amber-800 border-amber-200',
};

const confidenceLabels = {
  high: 'High confidence',
  needs_manual_review: 'Needs manual review',
};

const linkMethodLabels = {
  batch_ref: 'batch_ref match',
  helper: 'via nearest helper',
  release_ts: 'via ASN release time',
};

export default function AdminStockReconciliation() {
  const token = localStorage.getItem('mr_admin_token') || '';
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [scopeInput, setScopeInput] = useState('');
  const [approvals, setApprovals] = useState({}); // {pid: true}
  const [search, setSearch] = useState('');
  const [applyResult, setApplyResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [expandedAsn, setExpandedAsn] = useState({}); // {asn|pid: true}
  const [showUnlinked, setShowUnlinked] = useState(false);

  const runDryRun = useCallback(async () => {
    setLoading(true);
    setApplyResult(null);
    setApprovals({});
    try {
      const product_ids = scopeInput
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const { data } = await axios.post(
        `${API}/admin/stock-reconciliation/dry-run`,
        { product_ids: product_ids.length ? product_ids : null },
        { headers: authHeaders },
      );
      setReport(data);
      setForbidden(false);
      if (!(data.items || []).length && !(data.unlinked_rows || []).length) {
        toast.message('No ASN double-bookings detected.');
      } else {
        toast.success(
          `${data.summary.products_affected} products · ${data.summary.asn_product_rows} ASN rows · ${data.summary.unlinked_row_count} unlinked`,
        );
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setForbidden(true);
        setReport(null);
      } else {
        toast.error(err.response?.data?.detail || 'Dry-run failed.');
      }
    } finally {
      setLoading(false);
    }
  }, [authHeaders, scopeInput]);

  const downloadCsv = useCallback(async () => {
    if (!report) return;
    try {
      const res = await axios.get(
        `${API}/admin/stock-reconciliation/export-csv`,
        { headers: authHeaders, params: { run_id: report.run_id }, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock_reconciliation_${report.run_id.slice(0, 8)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CSV download failed.');
    }
  }, [authHeaders, report]);

  const filteredAsnRows = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    return (report.asn_rows || []).filter((r) => {
      if (!q) return true;
      return (
        (r.sku || '').toLowerCase().includes(q)
        || (r.name || '').toLowerCase().includes(q)
        || r.product_id.toLowerCase().includes(q)
        || r.asn_number.toLowerCase().includes(q)
      );
    });
  }, [report, search]);

  const filteredItems = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    return (report.items || []).filter((r) => {
      if (!q) return true;
      return (
        (r.sku || '').toLowerCase().includes(q)
        || (r.name || '').toLowerCase().includes(q)
        || r.product_id.toLowerCase().includes(q)
      );
    });
  }, [report, search]);

  const toggleApproval = (pid) => {
    setApprovals((prev) => {
      const next = { ...prev };
      if (next[pid]) delete next[pid]; else next[pid] = true;
      return next;
    });
  };

  const selectAllHighConf = () => {
    if (!report) return;
    const next = {};
    for (const r of report.items) {
      if (r.confidence === 'high') next[r.product_id] = true;
    }
    setApprovals(next);
  };

  const clearApprovals = () => setApprovals({});

  const approvedItems = useMemo(() => {
    if (!report) return [];
    return (report.items || []).filter((r) => approvals[r.product_id]);
  }, [report, approvals]);

  const openConfirm = () => {
    if (!approvedItems.length) {
      toast.warning('Select at least one product to approve.');
      return;
    }
    setConfirmOpen(true);
  };

  const applyApproved = useCallback(async () => {
    if (!report || !approvedItems.length) return;
    setApplying(true);
    try {
      const payload = {
        run_id: report.run_id,
        approvals: approvedItems.map((r) => ({
          product_id: r.product_id,
          proposed_qty: r.proposed_qty,
        })),
      };
      const { data } = await axios.post(
        `${API}/admin/stock-reconciliation/apply`,
        payload,
        { headers: authHeaders },
      );
      setApplyResult(data);
      setConfirmOpen(false);
      toast.success(`Applied to ${data.applied_count} product(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed.');
    } finally {
      setApplying(false);
    }
  }, [authHeaders, report, approvedItems]);

  // ---------------------------------------------------------------- render

  if (forbidden) {
    return (
      <AdminLayout>
        <div className="p-8 max-w-2xl mx-auto">
          <div className="bg-rose-50 border-2 border-rose-200 rounded-xl p-6 flex gap-3 items-start">
            <ShieldAlert className="w-6 h-6 text-rose-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-rose-900 text-lg">Super admin only</div>
              <p className="text-sm text-rose-800 mt-1">
                The Stock Reconciliation tool is restricted to the super admin
                because it writes corrective ledger rows.
              </p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="stock-reconciliation-page">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-neutral-900">
              Stock Reconciliation
            </h1>
            <p className="text-sm text-neutral-600 max-w-3xl mt-1">
              Detect ASN double-bookings per <span className="font-semibold">ASN × product</span>
              {' '}(no time window) and apply a single corrective ledger row per approved product.{' '}
              <span className="font-semibold text-neutral-800">
                Existing stock_events, placements, and batches are never edited or deleted.
              </span>
            </p>
          </div>
        </header>

        {/* --- Scope + run controls --- */}
        <section data-testid="scope-panel" className="bg-white border border-neutral-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <label htmlFor="scope-input" className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-1">
                Scope (product IDs, optional)
              </label>
              <textarea
                id="scope-input"
                data-testid="scope-input"
                value={scopeInput}
                onChange={(e) => setScopeInput(e.target.value)}
                rows={2}
                placeholder="Paste up to a few product IDs for a test run. Leave empty to scan every released ASN."
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
              <p className="text-[11px] text-neutral-500 mt-1 leading-snug">
                <Info className="w-3 h-3 inline-block -mt-0.5" /> Comma or space separated. Recommended: scope to 1–2 products first.
              </p>
            </div>
            <div className="flex items-end gap-2">
              <button
                type="button"
                data-testid="run-dry-run-btn"
                onClick={runDryRun}
                disabled={loading}
                className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-neutral-800 disabled:opacity-50 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition shadow-sm"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Run dry-run
              </button>
              {report && (
                <button
                  type="button"
                  data-testid="download-csv-btn"
                  onClick={downloadCsv}
                  className="inline-flex items-center gap-2 bg-white hover:bg-neutral-50 border border-neutral-300 text-neutral-800 font-semibold text-sm px-4 py-2.5 rounded-lg transition shadow-sm"
                >
                  <Download className="w-4 h-4" />
                  CSV
                </button>
              )}
            </div>
          </div>
        </section>

        {/* --- Dry-run summary --- */}
        {report && (
          <section data-testid="summary-panel" className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <SummaryCard label="ASNs scanned" value={report.summary.asns_scanned} icon={Package} tone="neutral" />
            <SummaryCard label="ASN rows w/ excess" value={report.summary.asn_rows_with_excess} icon={TrendingDown} tone="rose" />
            <SummaryCard label="High confidence" value={report.summary.high_confidence_count} icon={CheckCircle2} tone="emerald" />
            <SummaryCard label="Needs manual review" value={report.summary.needs_manual_review_count} icon={AlertTriangle} tone="amber" />
            <SummaryCard label="Unlinked rows" value={report.summary.unlinked_row_count} icon={Unlink} tone="neutral" />
          </section>
        )}

        {/* --- Per-product rollup (approval surface) --- */}
        {report && (report.items || []).length > 0 && (
          <section data-testid="proposals-panel" className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden">
            <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-4 border-b border-neutral-200">
              <div>
                <div className="font-bold text-neutral-900">Per-product rollup</div>
                <div className="text-xs text-neutral-500">
                  run_id <span className="font-mono">{report.run_id.slice(0, 8)}</span>
                  {' · '}
                  tolerance ±{report.summary.high_confidence_tolerance} ctn
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  data-testid="search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filter sku / name / id / ASN…"
                  className="rounded-lg border border-neutral-300 px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
                <button
                  type="button"
                  data-testid="select-all-high-conf-btn"
                  onClick={selectAllHighConf}
                  className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg"
                >
                  Select all high-conf
                </button>
                <button
                  type="button"
                  data-testid="clear-approvals-btn"
                  onClick={clearApprovals}
                  className="text-xs font-semibold text-neutral-700 hover:text-neutral-900 border border-neutral-300 bg-white hover:bg-neutral-50 px-3 py-1.5 rounded-lg"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-neutral-50 text-neutral-600 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left w-8"></th>
                    <th className="px-3 py-2 text-left">Product</th>
                    <th className="px-3 py-2 text-right">Current ledger</th>
                    <th className="px-3 py-2 text-right">Total excess</th>
                    <th className="px-3 py-2 text-right">Placements</th>
                    <th className="px-3 py-2 text-right">Proposed</th>
                    <th className="px-3 py-2 text-right">Remaining gap</th>
                    <th className="px-3 py-2 text-left">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((r) => {
                    const checked = !!approvals[r.product_id];
                    const disabled = r.has_net_negative_alert;
                    return (
                      <tr key={r.product_id} data-testid={`row-${r.product_id}`} className={`${disabled ? 'bg-amber-50/50' : ''} border-t border-neutral-100 hover:bg-neutral-50`}>
                        <td className="px-3 py-2 align-top">
                          <input
                            type="checkbox"
                            data-testid={`approve-${r.product_id}`}
                            checked={checked}
                            disabled={disabled}
                            onChange={() => toggleApproval(r.product_id)}
                            className="w-4 h-4 accent-emerald-600 disabled:opacity-30"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-neutral-900">{r.sku || '—'}</div>
                          <div className="text-xs text-neutral-600 line-clamp-1">{r.name || '—'}</div>
                          <div className="text-[10px] text-neutral-400 mt-0.5">{r.asn_count} ASN{r.asn_count === 1 ? '' : 's'}</div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-neutral-900">{r.current_stored_stock}</td>
                        <td className="px-3 py-2 text-right font-mono text-rose-700">−{r.total_excess_qty}</td>
                        <td className="px-3 py-2 text-right font-mono text-neutral-900">{r.placements_total}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-emerald-800">{r.proposed_qty}</td>
                        <td className={`px-3 py-2 text-right font-mono ${Math.abs(r.remaining_gap_after_excess) <= (report.summary.high_confidence_tolerance || 2) ? 'text-neutral-600' : 'text-amber-700'}`}>
                          {r.remaining_gap_after_excess > 0 ? '+' : ''}{r.remaining_gap_after_excess}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            data-testid={`confidence-${r.product_id}`}
                            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${confidenceStyles[r.confidence]}`}
                          >
                            {r.confidence === 'high' ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                            {confidenceLabels[r.confidence]}
                          </span>
                          {r.has_net_negative_alert && (
                            <div className="text-[10px] text-amber-800 mt-1 font-medium">
                              Ledger net-negative alert — auto-apply blocked.
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* --- Approval footer --- */}
            <div className="px-5 py-4 border-t border-neutral-200 bg-neutral-50 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-neutral-700">
                <span className="font-bold text-neutral-900">{approvedItems.length}</span> approved for correction
              </div>
              <button
                type="button"
                data-testid="review-and-apply-btn"
                onClick={openConfirm}
                disabled={!approvedItems.length}
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-semibold text-sm px-5 py-2.5 rounded-lg shadow-sm"
              >
                <PlayCircle className="w-4 h-4" />
                Review &amp; apply
              </button>
            </div>
          </section>
        )}

        {/* --- ASN booked rows (expandable per ASN × product) --- */}
        {report && filteredAsnRows.length > 0 && (
          <section data-testid="asn-rows-panel" className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-neutral-200">
              <div className="font-bold text-neutral-900">ASN × product booked rows</div>
              <div className="text-xs text-neutral-500">
                One row per ASN × product. Click a row to see each `stock_events` row that was booked.
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-neutral-50 text-neutral-600 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2 text-left">ASN</th>
                    <th className="px-3 py-2 text-left">Product</th>
                    <th className="px-3 py-2 text-right">Scanned</th>
                    <th className="px-3 py-2 text-right">Rows</th>
                    <th className="px-3 py-2 text-right">Total booked</th>
                    <th className="px-3 py-2 text-right">Excess</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAsnRows.map((r) => {
                    const key = `${r.asn_number}|${r.product_id}`;
                    const open = !!expandedAsn[key];
                    return (
                      <React.Fragment key={key}>
                        <tr
                          data-testid={`asn-row-${r.asn_number}-${r.product_id}`}
                          onClick={() => setExpandedAsn((prev) => ({ ...prev, [key]: !prev[key] }))}
                          className={`cursor-pointer border-t border-neutral-100 hover:bg-neutral-50 ${r.excess > 0 ? 'bg-rose-50/40' : ''}`}
                        >
                          <td className="px-3 py-2">
                            {open ? <ChevronDown className="w-4 h-4 text-neutral-600" /> : <ChevronRight className="w-4 h-4 text-neutral-500" />}
                          </td>
                          <td className="px-3 py-2 font-mono font-semibold text-neutral-900">{r.asn_number}</td>
                          <td className="px-3 py-2">
                            <div className="font-semibold">{r.sku || '—'}</div>
                            <div className="text-[11px] text-neutral-600 line-clamp-1">{r.name}</div>
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{r.scanned_qty}</td>
                          <td className="px-3 py-2 text-right font-mono">{r.booked_row_count}</td>
                          <td className="px-3 py-2 text-right font-mono">{r.total_booked}</td>
                          <td className={`px-3 py-2 text-right font-mono font-bold ${r.excess > 0 ? 'text-rose-700' : 'text-neutral-500'}`}>
                            {r.excess > 0 ? `+${r.excess}` : r.excess}
                          </td>
                        </tr>
                        {open && (
                          <tr className="bg-neutral-50/60 border-t border-neutral-100">
                            <td></td>
                            <td colSpan={6} className="px-3 py-2">
                              {r.booked_rows.length === 0 ? (
                                <div className="text-[12px] text-neutral-500 italic">No booked rows found for this ASN × product.</div>
                              ) : (
                                <table className="w-full text-[12px]">
                                  <thead>
                                    <tr className="text-neutral-500 border-b border-neutral-200">
                                      <th className="py-1 pr-3 text-left font-semibold">#</th>
                                      <th className="py-1 pr-3 text-left font-semibold">row id</th>
                                      <th className="py-1 pr-3 text-left font-semibold">date / time</th>
                                      <th className="py-1 pr-3 text-left font-semibold">source</th>
                                      <th className="py-1 pr-3 text-left font-semibold">batch_ref</th>
                                      <th className="py-1 pr-3 text-right font-semibold">qty</th>
                                      <th className="py-1 pr-3 text-left font-semibold">link</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {r.booked_rows.map((br, idx) => (
                                      <tr key={br.id} className="font-mono">
                                        <td className="py-1 pr-3 text-neutral-500">{idx + 1}</td>
                                        <td className="py-1 pr-3">{(br.id || '').slice(0, 8)}</td>
                                        <td className="py-1 pr-3">{br.ts}</td>
                                        <td className="py-1 pr-3">{br.source || <span className="italic text-neutral-400">—</span>}</td>
                                        <td className="py-1 pr-3">{br.batch_ref || <span className="italic text-neutral-400">—</span>}</td>
                                        <td className="py-1 pr-3 text-right">{br.qty}</td>
                                        <td className="py-1 pr-3">
                                          <span className="inline-flex items-center gap-1 text-[10px] bg-neutral-100 border border-neutral-200 rounded px-1.5 py-0.5">
                                            <LinkIcon className="w-3 h-3" />
                                            {linkMethodLabels[br.link_method] || br.link_method}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* --- Unlinked rows (not counted) --- */}
        {report && (report.unlinked_rows || []).length > 0 && (
          <section data-testid="unlinked-panel" className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden">
            <button
              type="button"
              data-testid="toggle-unlinked-btn"
              onClick={() => setShowUnlinked((v) => !v)}
              className="w-full px-5 py-3 flex items-center justify-between hover:bg-neutral-50 border-b border-neutral-200"
            >
              <div className="flex items-center gap-2">
                <Unlink className="w-4 h-4 text-amber-600" />
                <span className="font-bold text-neutral-900">
                  Unlinked rows ({report.unlinked_rows.length})
                </span>
                <span className="text-xs text-neutral-500">
                  — these rows could not be tied to a released ASN and are NOT counted toward any excess.
                </span>
              </div>
              {showUnlinked ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {showUnlinked && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-neutral-50 text-neutral-600 text-[11px] uppercase tracking-wider">
                    <tr>
                      <th className="px-3 py-2 text-left">Product</th>
                      <th className="px-3 py-2 text-left">row id</th>
                      <th className="px-3 py-2 text-left">date / time</th>
                      <th className="px-3 py-2 text-left">source</th>
                      <th className="px-3 py-2 text-left">batch_ref</th>
                      <th className="px-3 py-2 text-right">qty</th>
                      <th className="px-3 py-2 text-left">reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.unlinked_rows.map((u) => (
                      <tr key={u.id} data-testid={`unlinked-${u.id}`} className="border-t border-neutral-100 font-mono text-[12px]">
                        <td className="px-3 py-1.5">
                          <div className="font-semibold">{u.sku || '—'}</div>
                          <div className="text-[11px] text-neutral-500 line-clamp-1">{u.name}</div>
                        </td>
                        <td className="px-3 py-1.5">{(u.id || '').slice(0, 8)}</td>
                        <td className="px-3 py-1.5">{u.ts}</td>
                        <td className="px-3 py-1.5">{u.source || <span className="italic text-neutral-400">—</span>}</td>
                        <td className="px-3 py-1.5">{u.batch_ref || <span className="italic text-neutral-400">—</span>}</td>
                        <td className="px-3 py-1.5 text-right">{u.qty}</td>
                        <td className="px-3 py-1.5 text-amber-700">{u.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {report && !(report.items || []).length && !(report.unlinked_rows || []).length && !loading && (
          <div data-testid="empty-state" className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 flex gap-3 items-start">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div className="text-sm text-emerald-900">
              No ASN double-bookings detected in this scope. Nothing to correct.
            </div>
          </div>
        )}

        {/* --- Confirm dialog --- */}
        {confirmOpen && (
          <div className="fixed inset-0 z-50 bg-neutral-900/60 flex items-center justify-center p-4" data-testid="confirm-dialog">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-neutral-900">Confirm correction</h2>
              <p className="text-sm text-neutral-600">
                A new <span className="font-mono font-semibold">reconciliation_adjustment</span>
                {' '}(or <span className="font-mono font-semibold">_out</span>) row will be written per product below.
                Source tag: <span className="font-mono font-semibold">manual_reconciliation_288272</span>.
                No existing rows will be touched.
              </p>
              <div className="border border-neutral-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 text-[11px] uppercase text-neutral-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Product</th>
                      <th className="px-3 py-2 text-right">Current</th>
                      <th className="px-3 py-2 text-right">Proposed</th>
                      <th className="px-3 py-2 text-right">Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvedItems.map((r) => {
                      const delta = r.proposed_qty - r.current_stored_stock;
                      return (
                        <tr key={r.product_id} className="border-t border-neutral-100">
                          <td className="px-3 py-2">
                            <div className="font-semibold">{r.sku || r.product_id.slice(0, 8)}</div>
                            <div className="text-xs text-neutral-500 line-clamp-1">{r.name}</div>
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{r.current_stored_stock}</td>
                          <td className="px-3 py-2 text-right font-mono font-bold">{r.proposed_qty}</td>
                          <td className={`px-3 py-2 text-right font-mono font-bold ${delta < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                            {delta > 0 ? '+' : ''}{delta}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  data-testid="cancel-apply-btn"
                  onClick={() => setConfirmOpen(false)}
                  disabled={applying}
                  className="px-4 py-2 rounded-lg border border-neutral-300 text-sm font-semibold text-neutral-700 hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="confirm-apply-btn"
                  onClick={applyApproved}
                  disabled={applying}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-sm disabled:opacity-50"
                >
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Write corrections
                </button>
              </div>
            </div>
          </div>
        )}

        {/* --- Apply result --- */}
        {applyResult && (
          <section data-testid="apply-result-panel" className="bg-white border-2 border-emerald-300 rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 bg-emerald-50 border-b border-emerald-200">
              <div className="font-bold text-emerald-900">
                Applied — {applyResult.applied_count} corrected
                {applyResult.skipped_count > 0 && `, ${applyResult.skipped_count} skipped`}
              </div>
              <div className="text-xs text-emerald-800 mt-0.5">{applyResult.applied_at}</div>
            </div>
            {applyResult.applied.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-[11px] uppercase text-neutral-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Product</th>
                    <th className="px-3 py-2 text-right">Was</th>
                    <th className="px-3 py-2 text-right">Delta</th>
                    <th className="px-3 py-2 text-right">New net</th>
                    <th className="px-3 py-2 text-left">Event id</th>
                  </tr>
                </thead>
                <tbody>
                  {applyResult.applied.map((r) => (
                    <tr key={r.product_id} data-testid={`applied-${r.product_id}`} className="border-t border-neutral-100">
                      <td className="px-3 py-2">
                        <div className="font-semibold">{r.sku || r.product_id.slice(0, 8)}</div>
                        <div className="text-xs text-neutral-500 line-clamp-1">{r.name}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{r.current_stored_stock}</td>
                      <td className={`px-3 py-2 text-right font-mono font-bold ${r.delta < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                        {r.delta > 0 ? '+' : ''}{r.delta}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-bold">{r.new_ledger_net}</td>
                      <td className="px-3 py-2 font-mono text-xs text-neutral-600">{(r.event_written || '').slice(0, 8)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {applyResult.skipped.length > 0 && (
              <div className="px-5 py-3 border-t border-neutral-200 bg-amber-50">
                <div className="text-xs font-bold text-amber-900 uppercase tracking-wider mb-1">Skipped</div>
                <ul className="text-xs text-amber-900 space-y-0.5">
                  {applyResult.skipped.map((s) => (
                    <li key={s.product_id} className="font-mono">
                      {s.product_id} · {s.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}
      </div>
    </AdminLayout>
  );
}

function SummaryCard({ label, value, icon: Icon, tone = 'neutral' }) {
  const toneClass = {
    neutral: 'bg-white border-neutral-200 text-neutral-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
  }[tone];
  return (
    <div className={`rounded-xl border px-4 py-3 shadow-sm ${toneClass}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold tracking-wider uppercase opacity-70">{label}</span>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      <div className="text-2xl font-black mt-1 tabular-nums">{value}</div>
    </div>
  );
}
